import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  InfoOutlined as InfoOutlinedIcon,
} from "@mui/icons-material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import LockIcon from "@mui/icons-material/Lock";
import {
  Box,
  Card,
  CardActionArea,
  Collapse,
  IconButton,
  Typography,
} from "@mui/material";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  selectFiles,
  selectFormData,
  updateFileField,
  updateFormField,
} from '../../../Store/userSlice';
import FileUploadField from "../../common/FileUpload/FileUploadField";
import { convertAndSetFile, getFinancialYear } from "../../common/Util/Util";
import {
  entityDocUploadCompanyValidation,
  entityDocUploadProprietorshipValidation,
} from "../../common/ValidationRules/validationRules";
import "./BlurredDoc.css";
import { fetchCompanyDocbyCIN } from '../../../services/loanService';

const EntityDocuments = forwardRef((props, ref) => {
  const { currentEntityType } = props;
  const dispatch = useDispatch();
  const formData = useSelector(selectFormData);
  const files = useSelector(selectFiles);

  // Section toggles
  const [expanded, setExpanded] = useState({
    company: false,
    directors: false,
    partnership: false,
    proprietorship: false,
    financials: false,
  });

  const handleDocmentFetch = () => {
    if (formData?.companyCin) {
      const resetMcaFields = () => {
        dispatch(updateFormField({ field: "datafetchedFromportal", value: false }));
        dispatch(updateFormField({ field: "mcaDocuments", value: [] }));
        dispatch(updateFormField({ field: "mcaDocxStatus", value: undefined }));
      };
      fetchCompanyDocbyCIN(formData?.companyCin)
        .then((res) => {
          if (res?.statusCode === 200) {
            const documents = res?.data?.documents ?? res?.documents ?? [];
            dispatch(
              updateFormField({
                field: "datafetchedFromportal",
                value: true,
              })
            );
            dispatch(
              updateFormField({
                field: "mcaDocuments",
                value: documents,
              })
            );
            dispatch(
              updateFormField({
                field: "mcaDocxStatus",
                value: res?.data?.mcaDocxStatus ?? res?.mcaDocxStatus ?? true,
              })
            );
            toast.success("Documents fetched successfully! You can now proceed further.");
          } else {
            resetMcaFields();
          }
        })
        .catch(resetMcaFields);
    }
  };
  const toggleSection = (section) => {
    setExpanded((prev) => {
      const allFalse = Object.keys(prev).reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {});
      return {
        ...allFalse,
        [section]: !prev[section], // open if closed
      };
    });
  };

  const [isMobile, setIsMobile] = useState(false);

  const {
    control,
    formState: { errors },
    setValue,
    trigger,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      borrowerPanCard: null,
      moa: null,
      aoa: null,
      listOfDirector: null,
      shareholding: null,
      listOfPatner: null,
      legalDocuments: null,
      provisionalBalanceSheet: null,
      auditedBalanceFYPrev1: null,
      auditedBalanceFYPrev2: null,
      datafetchedFromportal: false,
    },
  });


  useImperativeHandle(ref, () => ({
    validateForm: async () => {
      return formData?.datafetchedFromportal
        ? await trigger("provisionalBalanceSheet")
        : await trigger();
    },
  }));

  // Determine current date and financial year
  const currentDate = useMemo(() => new Date(), []);
  const currentFinancialYear = useMemo(
    () => getFinancialYear(currentDate),
    [currentDate]
  );

  const isProvisionalAsAudited = useMemo(() => {
    const month = currentDate.getMonth() + 1;
    return month >= 7;
  }, [currentDate]);

  const isProvisionalRequired = useMemo(
    () => isProvisionalAsAudited,
    [isProvisionalAsAudited]
  );

  console.log("BalanceSheet:", files.balanceSheet);
  // Set default values and update form values based on files and formData
  useEffect(() => {
    // for partners
    setValue("partnersList", convertAndSetFile(files?.listOfPatner));
    setValue(
      "datafetchedFromportal",
      convertAndSetFile(formData?.datafetchedFromportal)
    );

    setValue("companyPan", convertAndSetFile(files?.borrowerPanCard));
    setValue(
      "provisionalBalanceSheet",
      convertAndSetFile(files?.provisionalBalanceSheet)
    );
    setValue("companyMoa", convertAndSetFile(files?.moa));
    setValue("companyAoa", convertAndSetFile(files?.aoa));
    setValue("directorsList", convertAndSetFile(files?.listOfDirector));
    setValue(
      "shareholdingPattern",
      convertAndSetFile(files?.shareholding)
    );

    setValue(
      "auditedBalanceFYPrev1",
      convertAndSetFile(files.auditedBalanceFYPrev1)
    );
    setValue(
      "auditedBalanceFYPrev2",
      convertAndSetFile(files.auditedBalanceFYPrev2)
    );
  }, [files, formData, setValue]);


  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  const handleFileSelect = (fieldName) => async (selectedFile) => {
    try {
      if (selectedFile) {
        const base64 = await fileToBase64(selectedFile);
        dispatch(
          updateFileField({
            field: fieldName,
            value: { base64, filename: selectedFile.name },
          })
        );
      }
    } catch (error) {
      console.error("Failed to convert file to base64:", error);
    }
  };


  // detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 600);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // ========== Form sections (simplified for readability) ==========

  const CompanyDocs = () => (
    <Box>
      <Box
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          overflow: "hidden",
          mb: 3,
        }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            //   mb:3,
            backgroundColor: "#f5f5f5",
            "&:hover": { backgroundColor: "#eeeeee" },
          }}
          onClick={() => toggleSection("company")}
        >
          <Typography variant="h6">Upload Entity Documents</Typography>
          <IconButton>
            {expanded.company ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Collapse in={expanded.company}>
          <Box
            sx={{
              p: 3,
              borderTop: "1px solid #e0e0e0",
              backgroundColor: "white",
            }}
          >
            <Controller
              name="borrowerPanCard"
              control={control}
              rules={entityDocUploadCompanyValidation.companyPan}
              render={({ field }) => (
                <FileUploadField
                  name="borrowerPanCard"
                  label={
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      PAN (Permanent Account Number)
                    </Box>
                  }
                  error={!!errors.companyPan}
                  helperText={errors.companyPan?.message}
                  value={convertAndSetFile(files?.borrowerPanCard)}
                  onFileSelect={(file) => {
                    field.onChange(file);
                    handleFileSelect("borrowerPanCard")(file);
                  }}
                />
              )}
            />

            {currentEntityType === "Partnership Firm" && (
              <Controller
                name="incorporationProof"
                control={control}
                rules={
                  entityDocUploadProprietorshipValidation.incorporationProof
                }
                render={({ field }) => (
                  <FileUploadField
                    name="incorporationProof"
                    label={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        Incorporation Date Proof
                      </Box>
                    }
                    error={!!errors.incorporationProof}
                    helperText={errors.incorporationProof?.message}
                    value={convertAndSetFile(
                      files?.incorporationDateProof
                    )}
                    onFileSelect={(file) => {
                      field.onChange(file);
                      handleFileSelect("incorporationDateProof")(file);
                    }}
                  />
                )}
              />
            )}

            {/* company documents */}
            {currentEntityType !== "Proprietorship Firm" &&
              currentEntityType !== "Partnership Firm" && (
                <>
                  <Controller
                    name="companyMoa"
                    control={control}
                    rules={entityDocUploadCompanyValidation.companyMoa}
                    render={({ field }) => (
                      <FileUploadField
                        name="companyMoa"
                        label={
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            MOA (Memorandum of Association)
                          </Box>
                        }
                        error={!!errors.companyMoa}
                        helperText={errors.companyMoa?.message}
                        value={convertAndSetFile(files?.moa)}
                        onFileSelect={(file) => {
                          field.onChange(file);
                          handleFileSelect("moa")(file);
                        }}
                      />
                    )}
                  />
                  <Controller
                    name="companyAoa"
                    control={control}
                    rules={entityDocUploadCompanyValidation.companyAoa}
                    render={({ field }) => (
                      <FileUploadField
                        name="companyAoa"
                        label={
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            AOA (Articles of Association)
                          </Box>
                        }
                        error={!!errors.companyAoa}
                        helperText={errors.companyAoa?.message}
                        value={convertAndSetFile(files?.aoa)}
                        onFileSelect={(file) => {
                          field.onChange(file);
                          handleFileSelect("aoa")(file);
                        }}
                      />
                    )}
                  />
                  <Controller
                    name="shareholdingPattern"
                    control={control}
                    rules={entityDocUploadCompanyValidation.shareholdingPattern}
                    render={({ field }) => (
                      <FileUploadField
                        name="shareholdingPattern"
                        label={
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            Share holding Pattern
                          </Box>
                        }
                        error={!!errors.shareholdingPattern}
                        helperText={errors.shareholdingPattern?.message}
                        value={convertAndSetFile(files?.shareholding)}
                        onFileSelect={(file) => {
                          field.onChange(file);
                          handleFileSelect("shareholding")(file);
                        }}
                      />
                    )}
                  />
                </>
              )}
          </Box>
        </Collapse>
      </Box>
    </Box>
  );

  // Financial Documents Section

  const renderProvisionalBalanceSheet = () => (
    <Controller
      name="provisionalBalanceSheet"
      control={control}
      rules={
        isProvisionalRequired
          ? entityDocUploadCompanyValidation.auditedBalanceFYPrev1
          : {}
      }
      render={({ field }) => (
        <FileUploadField
          name="provisionalBalanceSheet"
          label={
            <Box sx={{ display: "flex", alignItems: "center" }}>
              Provisional Balance Sheet FY {currentFinancialYear}
              {isProvisionalRequired && (
                <span style={{ color: "red", marginLeft: "4px" }}>*</span>
              )}
            </Box>
          }
          error={!!errors.provisionalBalanceSheet}
          helperText={errors.provisionalBalanceSheet?.message}
          value={convertAndSetFile(files?.provisionalBalanceSheet)}
          onFileSelect={(file) => {
            field.onChange(file);
            handleFileSelect("provisionalBalanceSheet")(file);
          }}
        />
      )}
    />
  );

  const renderManualPanUpload = () => (
    <Controller
      name="borrowerPanCard"
      control={control}
      rules={entityDocUploadCompanyValidation.companyPan}
      render={({ field }) => (
        <FileUploadField
          name="borrowerPanCard"
          label={
            <Box sx={{ display: "flex", alignItems: "center" }}>
              PAN (Permanent Account Number)
            </Box>
          }
          error={!!errors.companyPan}
          helperText={errors.companyPan?.message}
          value={convertAndSetFile(files?.borrowerPanCard)}
          onFileSelect={(file) => {
            field.onChange(file);
            handleFileSelect("borrowerPanCard")(file);
          }}
        />
      )}
    />
  );

  const FinancialDocs = () => (
    <Box>
      {/* {" "} */}
      {/* Audited balance sheets etc. */}
      <Box
        sx={{
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          overflow: "hidden",
          mb: 3,
        }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            backgroundColor: "#f5f5f5",
            "&:hover": { backgroundColor: "#eeeeee" },
          }}
          onClick={() => toggleSection("financials")}
        >
          <Typography variant="h6">
            Upload Financial Reports
            {isProvisionalRequired &&
              !(formData?.datafetchedFromportal || formData?.mcaDocxStatus) && (
                <span style={{ color: "red", marginLeft: "2px" }}>*</span>
              )}
          </Typography>
          <IconButton>
            {expanded?.financials ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Collapse in={expanded?.financials}>
          <Box
            sx={{
              p: 3,
              borderTop: "1px solid #e0e0e0",
              backgroundColor: "white",
            }}
          >
            {!formData.datafetchedFromportal && renderProvisionalBalanceSheet()}

            {false && (
              <Controller
                name="auditedBalanceFYPrev1"
                control={control}
                rules={entityDocUploadCompanyValidation.auditedBalanceFYPrev1}
                render={({ field }) => (
                  <FileUploadField
                    name="auditedBalanceFYPrev1"
                    label={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        Audited Balance Sheet FY{" "}
                        {(() => {
                          const prevYear = currentDate.getFullYear() - 1;
                          const prevMonth = currentDate.getMonth() + 1;
                          if (prevMonth >= 4) {
                            return `${prevYear}-${prevYear + 1}`;
                          } else {
                            return `${prevYear - 1}-${prevYear}`;
                          }
                        })()}
                        <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                      </Box>
                    }
                    error={!!errors.auditedBalanceFYPrev1}
                    helperText={errors.auditedBalanceFYPrev1?.message}
                    value={convertAndSetFile(files?.auditedBalanceFYPrev1)}
                    onFileSelect={(file) => {
                      field.onChange(file);
                      handleFileSelect("auditedBalanceFYPrev1")(file);
                    }}
                  />
                )}
              />
            )}

            {false && (
              <Controller
                name="auditedBalanceFYPrev2"
                control={control}
                rules={
                  formData.datafetchedFromportal
                    ? {}
                    : entityDocUploadCompanyValidation.auditedBalanceFYPrev2
                }
                render={({ field }) => (
                  <FileUploadField
                    name="auditedBalanceFYPrev2"
                    label={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        Audited Balance Sheet FY{" "}
                        {(() => {
                          const prevYear = currentDate.getFullYear() - 2;
                          const prevMonth = currentDate.getMonth() + 1;
                          if (prevMonth >= 4) {
                            return `${prevYear}-${prevYear + 1}`;
                          } else {
                            return `${prevYear - 1}-${prevYear}`;
                          }
                        })()}
                        <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                      </Box>
                    }
                    error={!!errors.auditedBalanceFYPrev2}
                    helperText={errors.auditedBalanceFYPrev2?.message}
                    value={convertAndSetFile(files?.auditedBalanceFYPrev2)}
                    onFileSelect={(file) => {
                      field.onChange(file);
                      handleFileSelect("auditedBalanceFYPrev2")(file);
                    }}
                  />
                )}
              />
            )}
          </Box>
        </Collapse>
      </Box>
    </Box>
  );

  // ========== Final Render ==========

  return (
    <Box>
      <Typography variant="body2" sx={{ color: "#666", mb: 3 }}>
        Step 2/5
      </Typography>
      {/* Fetch or Upload choice */}
      {formData?.companyCin && (
        <Box sx={{ mb: 2, textAlign: "right" }}>
          {" "}
          {/* 🔹 align right */}
          <Card
            sx={{
              maxWidth: 240, // 🔹 even smaller
              borderRadius: 2,
              boxShadow: 2,
              background: (formData?.datafetchedFromportal || formData?.mcaDocxStatus)
                ? "linear-gradient(135deg, #B0BEC5, #90A4AE)" // 🔹 greyed out if disabled
                : "linear-gradient(135deg, #4CAF50, #2E7D32)",
              color: "white",
              opacity: (formData?.datafetchedFromportal || formData?.mcaDocxStatus) ? 0.6 : 1, // 🔹 faded if disabled
              pointerEvents: (formData?.datafetchedFromportal || formData?.mcaDocxStatus) ? "none" : "auto", // 🔹 disable click
            }}
          >
            <CardActionArea
              onClick={() =>
                !(formData?.datafetchedFromportal || formData?.mcaDocxStatus) &&
                handleDocmentFetch()
              }
              sx={{
                p: 1, // smaller padding
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <CloudDownloadIcon sx={{ fontSize: 16, mb: 0.3 }} />{" "}
              {/* smaller icon */}
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Fetch Documents
              </Typography>
              <Typography
                variant="caption"
                sx={{ mt: 0.2, opacity: 0.9, textAlign: "center" }}
              >
                Securely fetch from govt portal
              </Typography>
            </CardActionArea>
          </Card>
        </Box>
      )}

      {/* Info Bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 3,
          justifyContent: "flex-end",
        }}
      >
        <Typography>
          Upload files in JPEG, PNG, or PDF format, up to 5 MB in size.
        </Typography>
        <InfoOutlinedIcon sx={{ ml: 1, color: "#666", fontSize: 20 }} />
      </Box>

      {(formData?.datafetchedFromportal || formData?.mcaDocxStatus) ? (
        <>
          <div className="upload-container">
            {/* Blur overlay */}
            <div className="blur-layer" />

            {/* Overlay Text with Lock */}
            <div className={`overlay-text ${isMobile ? "wrap-text" : ""}`}>
              <LockIcon
                style={{ fontSize: 48, color: "#666", marginBottom: "10px" }}
              />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  color: "text.secondary",
                }}
              >
                Documents fetched from government portal
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Section is locked and cannot be edited
              </Typography>
            </div>

            {/* Fields container - disabled */}
            <div className="field-content disabled">
              <>
                <FinancialDocs />
                <CompanyDocs />
              </>
            </div>
          </div>
          <Box
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              p: 3,
              mb: 3,
              backgroundColor: "white",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <LockIcon sx={{ color: "#4CAF50", fontSize: 28, mr: 1 }} />
              <Typography variant="h6" sx={{ mb: 0 }}>
                Upload Financial Reports
                {isProvisionalRequired &&
                  !(formData?.datafetchedFromportal || formData?.mcaDocxStatus) && (
                    <span style={{ color: "red", marginLeft: "2px" }}>*</span>
                  )}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
              Please upload the following financial documents manually, even though other documents were fetched from the government portal.
            </Typography>
            {renderProvisionalBalanceSheet()}
            
            {false && (
              <Controller
                name="auditedBalanceFYPrev1"
                control={control}
                rules={entityDocUploadCompanyValidation.auditedBalanceFYPrev1}
                render={({ field }) => (
                  <FileUploadField
                    name="auditedBalanceFYPrev1"
                    label={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        Audited Balance Sheet FY{" "}
                        {(() => {
                          const prevYear = currentDate.getFullYear() - 1;
                          const prevMonth = currentDate.getMonth() + 1;
                          if (prevMonth >= 4) {
                            return `${prevYear}-${prevYear + 1}`;
                          } else {
                            return `${prevYear - 1}-${prevYear}`;
                          }
                        })()}
                        <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                      </Box>
                    }
                    error={!!errors.auditedBalanceFYPrev1}
                    helperText={errors.auditedBalanceFYPrev1?.message}
                    value={convertAndSetFile(files?.auditedBalanceFYPrev1)}
                    onFileSelect={(file) => {
                      field.onChange(file);
                      handleFileSelect("auditedBalanceFYPrev1")(file);
                    }}
                  />
                )}
              />
            )}
            {false && (
              <Controller
                name="auditedBalanceFYPrev2"
                control={control}
                rules={
                  formData.datafetchedFromportal
                    ? {}
                    : entityDocUploadCompanyValidation.auditedBalanceFYPrev2
                }
                render={({ field }) => (
                  <FileUploadField
                    name="auditedBalanceFYPrev2"
                    label={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        Audited Balance Sheet FY{" "}
                        {(() => {
                          const prevYear = currentDate.getFullYear() - 2;
                          const prevMonth = currentDate.getMonth() + 1;
                          if (prevMonth >= 4) {
                            return `${prevYear}-${prevYear + 1}`;
                          } else {
                            return `${prevYear - 1}-${prevYear}`;
                          }
                        })()}
                        <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                      </Box>
                    }
                    error={!!errors.auditedBalanceFYPrev2}
                    helperText={errors.auditedBalanceFYPrev2?.message}
                    value={convertAndSetFile(files?.auditedBalanceFYPrev2)}
                    onFileSelect={(file) => {
                      field.onChange(file);
                      handleFileSelect("auditedBalanceFYPrev2")(file);
                    }}
                  />
                )}
              />
            )}
          </Box>
          <Box
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              p: 3,
              mb: 3,
              backgroundColor: "white",
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              Upload PAN (Permanent Account Number)
            </Typography>
            <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
              You can optionally upload the PAN card manually if you would like to provide the latest copy.
            </Typography>
            {renderManualPanUpload()}
          </Box>
        </>
      ) : (
        <>
          <FinancialDocs />
          <CompanyDocs />
        </>
      )}
    </Box>
  );
});

export default EntityDocuments;
